import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  Receipt,
  CreditCard,
  TrendingUp,
  Calendar,
  PoundSterling,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Download
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const MerchantBillingPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billingRes, transRes] = await Promise.all([
        fetch(`${API}/merchant/billing`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        }),
        fetch(`${API}/merchant/billing/transactions`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        })
      ]);

      if (billingRes.ok) {
        setBillingData(await billingRes.json());
      }
      if (transRes.ok) {
        setTransactions(await transRes.json());
      }
    } catch (error) {
      console.error("Error fetching billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-emerald-100 text-emerald-700",
      pending: "bg-amber-100 text-amber-700",
      overdue: "bg-red-100 text-red-700"
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status] || "bg-slate-100 text-slate-600"}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/merchant" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <img src={COMPANY_LOGO} alt="V" className="h-8 w-auto brightness-0" />
              <div className="h-6 w-px bg-slate-300" />
              <span className="font-brand text-sm font-bold text-slate-900">VEYRONIX</span>
            </div>
            <span className="text-sm text-slate-500">Billing & Invoices</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">Billing & Invoices</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {["overview", "invoices", "transactions"].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? "default" : "outline"}
              onClick={() => setActiveTab(tab)}
              className="rounded-full capitalize"
            >
              {tab}
            </Button>
          ))}
        </div>

        {activeTab === "overview" && billingData && (
          <div className="space-y-6">
            {/* Current Period Summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                Current Billing Period
              </h2>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="h-5 w-5 text-indigo-500" />
                    <span className="text-sm text-slate-500">Transactions</span>
                  </div>
                  <p className="text-2xl font-heading font-bold text-slate-900">
                    {billingData.current_period?.transactions || 0}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-emerald-500" />
                    <span className="text-sm text-slate-500">Transaction Revenue</span>
                  </div>
                  <p className="text-2xl font-heading font-bold text-slate-900">
                    £{(billingData.current_period?.transaction_revenue || 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <PoundSterling className="h-5 w-5 text-amber-500" />
                    <span className="text-sm text-slate-500">Transaction Fees ({billingData.current_period?.transaction_fee_percent}%)</span>
                  </div>
                  <p className="text-2xl font-heading font-bold text-slate-900">
                    £{(billingData.current_period?.transaction_fees || 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-5 w-5 text-cyan-500" />
                    <span className="text-sm text-slate-500">Period</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">
                    {billingData.current_period?.start ? new Date(billingData.current_period.start).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) : '-'}
                    {' - '}
                    {billingData.current_period?.end ? new Date(billingData.current_period.end).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Current Subscription */}
            {billingData.current_subscription && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                  Your Subscription
                </h2>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{billingData.current_subscription.plan_name}</p>
                    <p className="text-sm text-slate-500">
                      £{billingData.current_subscription.monthly_fee}/month base fee
                    </p>
                  </div>
                  <span className="bg-emerald-100 text-emerald-700 text-xs px-3 py-1 rounded-full">
                    Active
                  </span>
                </div>
              </div>
            )}

            {/* Billing Breakdown */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                How Billing Works
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-indigo-600">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Base Subscription Fee</p>
                    <p className="text-sm text-slate-500">Monthly fee based on your plan (Basic £149.99, Professional £249.99, Enterprise £399.99)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-indigo-600">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Platform Commission (2%)</p>
                    <p className="text-sm text-slate-500">Deducted automatically from each sale at checkout</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-indigo-600">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Transaction Fee (0.5%)</p>
                    <p className="text-sm text-slate-500">Small processing fee on each transaction, billed monthly</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "invoices" && (
          <div className="space-y-4">
            {billingData?.billings?.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-900 mb-2">No invoices yet</h3>
                <p className="text-slate-500">Your monthly invoices will appear here.</p>
              </div>
            ) : (
              billingData?.billings?.map((bill, idx) => (
                <div key={bill.billing_id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`invoice-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        bill.status === "paid" ? "bg-emerald-100" :
                        bill.status === "overdue" ? "bg-red-100" :
                        "bg-amber-100"
                      }`}>
                        {bill.status === "paid" ? (
                          <CheckCircle className="h-5 w-5 text-emerald-600" />
                        ) : bill.status === "overdue" ? (
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {new Date(bill.period_start).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                        </p>
                        <p className="text-sm text-slate-500 font-mono">{bill.billing_id}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-heading font-bold text-slate-900">
                        £{bill.total_amount?.toFixed(2)}
                      </p>
                      {getStatusBadge(bill.status)}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Base Plan</p>
                      <p className="font-semibold">£{bill.base_plan_amount?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Transactions</p>
                      <p className="font-semibold">{bill.transaction_count}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Transaction Fees</p>
                      <p className="font-semibold">£{bill.transaction_fees?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Period</p>
                      <p className="font-semibold">
                        {new Date(bill.period_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - 
                        {new Date(bill.period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "transactions" && (
          <div className="space-y-4">
            {transactions.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-900 mb-2">No transactions yet</h3>
                <p className="text-slate-500">Your transactions will appear here.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Order ID</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Platform Fee (2%)</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Transaction Fee (0.5%)</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {transactions.slice(0, 50).map((tx, idx) => (
                      <tr key={tx.order_id} data-testid={`transaction-row-${idx}`}>
                        <td className="px-6 py-4 font-mono text-sm">{tx.order_id}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-GB') : '-'}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold">£{(tx.total_amount || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-right text-red-600">-£{(tx.platform_fee || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-right text-red-600">-£{(tx.transaction_fee || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-right font-semibold text-emerald-600">
                          £{((tx.total_amount || 0) - (tx.total_fees || 0)).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MerchantBillingPage;
